/**
 * Survey periods as a person reads them (AIC-557): vendors and analysts never see a period code ("2026H1") as a label.
 * One helper for the vendor page, the sheet labels, the outreach drafts and the field-label copies, so none keeps its
 * own. The spoken form is voice/spoken.ts `spokenPeriod`.
 *
 *   "text"    running text:          "first half of 2026"   ("2025" stays "2025")
 *   "compact" headers and subjects:  "H1 2026"              ("2025" stays "2025")
 *
 * A code that is not a year, half or quarter ("2026H3", "latest") gives a plain "this period", never the code.
 */
export type PeriodLabelStyle = "text" | "compact";

const ORDINAL = ["", "first", "second", "third", "fourth"] as const;

export function periodLabel(period: string, style: PeriodLabelStyle = "text"): string {
  const m = /^(\d{4})(?:([HQ])([1-4]))?$/.exec(period.trim());
  if (!m) return "this period";
  const [, year, kind, n] = m;
  if (!kind) return year!;
  const num = Number(n);
  if (kind === "H" && num > 2) return "this period";
  if (style === "compact") return `${kind}${num} ${year}`;
  return `${ORDINAL[num]} ${kind === "H" ? "half" : "quarter"} of ${year}`;
}

/** Whether a period is a half year or quarter (the sheet's footnote only explains these). */
export const isPartYear = (period: string): boolean => /^\d{4}[HQ][1-4]$/.test(period.trim());

/** One id segment or cycle id as an analyst reads it (AIC-571): a half or quarter code becomes "H1 2026", anything else (a year, a key, "all") is unchanged. */
export const periodCodeLabel = (segment: string): string => (isPartYear(segment) && periodLabel(segment) !== "this period" ? periodLabel(segment, "compact") : segment);

/**
 * Bare half-year and quarter codes in prose ("2026H1" → "H1 2026"). Only whole codes are rewritten, never inside a URL, email, file name or hyphenated token, and not the tail of a
 * field id ("sw_rev_total:2026H1"), so nothing else in the sentence changes. A code that is no real period ("2026H3") stays.
 */
export const periodCodesInText = (text: string): string =>
  // URLs and emails (odd parts of the split) are kept whole; so are file names and hyphenated tokens ("report-2026H1.pdf", "2024Q1-release").
  text.split(/(\S+:\/\/\S+|\S+@\S+)/).map((part, i) => (i % 2 ? part : part.replace(/(?<![\w:/.@-])\d{4}[HQ][1-4](?![\w/@-]|:\S|\.\w)/g, periodCodeLabel))).join("");
